/**
 * lib/dexie/schema.ts
 * Schema de IndexedDB con Dexie.js
 *
 * CONVENCIÓN: Todos los campos usan snake_case para consistencia
 * con los nombres de columnas de Supabase. El mapeo se hace en
 * syncWorker.ts al momento de escribir en Dexie.
 */

import Dexie, { type EntityTable, type Table } from 'dexie'

// ── LocalProduct ───────────────────────────────────────────────────────────
export interface LocalProduct {
  id: string
  name: string
  code: string | null
  barcode: string | null
  brand: string | null
  category_id: string | null
  category_name: string | null
  category_emoji: string | null
  price_retail: number
  price_b: number | null
  price_c: number | null
  // P0 (00221): el costo NUNCA se sincroniza al cliente. El servidor es la única
  // autoridad del COGS (trigger 00219). Lectura autorizada vía RPC get_products_cost.
  tax_rate: number
  tax_included: boolean
  emoji: string | null
  photo_url: string | null
  featured: boolean
  track_stock: boolean
  active: boolean
  business_id: string | null // v6 — empresa dueña del producto (null = legacy global)
  synced_at: number // Date.now()
}

// ── LocalStock ─────────────────────────────────────────────────────────────
export interface LocalStock {
  id: string // `${product_id}_${store_id}`
  product_id: string
  store_id: string
  stock: number
  reserved: number
  available: number // stock - reserved (calculado)
  updated_at: string // ISO string
  synced_at: number // Date.now()
}

// ── LocalClient ────────────────────────────────────────────────────────────
export interface LocalClient {
  id: string
  name: string
  phone_e164: string
  email: string | null
  business_name?: string | null
  level: 'final' | 'pro' | 'vip'
  total_purchases: number
  total_visits: number
  last_visit: string | null // ISO string o null
  active: boolean
  synced_at: number // Date.now()
  // ALCANCE (0B.9 P1#5): PII de clientes cacheada SIN alcance permitía que, al cambiar
  // de tienda/empresa (sobre todo OFFLINE, sin reconciliación posible), siguieran
  // visibles los clientes de la tienda anterior. Con el alcance grabado, el filtro es
  // LOCAL e inmediato: los clientes de otro alcance dejan de verse al instante, y la
  // reconciliación posterior los purga físicamente.
  business_id?: string | null
  store_id?: string | null
}

// ── LocalCategory ──────────────────────────────────────────────────────────
export interface LocalCategory {
  id: string
  name: string
  parent_id: string | null
  emoji: string | null
  sort_order: number
  active: boolean
  business_id: string | null // v6 — empresa dueña de la categoría (null = legacy global)
  synced_at: number // Date.now()
}

// ── LocalShift ─────────────────────────────────────────────────────────────
export interface LocalShift {
  id: string
  store_id: string
  user_id: string
  status: 'open' | 'closed' | 'auto_closed' | 'force_closed'
  start_cash: number
  opened_at: string // ISO string
  expected_cash: number | null
  end_cash_total: number | null
  difference: number | null
  closed_at: string | null // ISO string o null
  synced_at: number // Date.now()
}

// ── LocalBusiness ──────────────────────────────────────────────────────────
// Caché offline de la tabla `businesses`. Mapeada por BusinessSnapshot.
export interface LocalBusiness {
  id: string
  brand: string // identificador corto único: 'CELMAXX', 'G-TIDE', 'VOLTUM'
  name: string
  legal_name: string | null
  rfc: string | null
  status: string // 'active' | 'trial' | 'suspended' | 'cancelled'
  license: Record<string, unknown> // BusinessLicense como JSONB
  config: Record<string, unknown> // CompanyConfig como JSONB
  created_at: string // ISO string
  updated_at: string // ISO string
  synced_at: number // Date.now()
}

// ── LocalStore ─────────────────────────────────────────────────────────────
// Caché offline de la tabla `stores`. Conectada a businesses vía business_id.
export interface LocalStore {
  id: string
  code: string
  brand: string // legacy — preservar como fallback hasta que stores.business_id esté en todos lados
  business_id: string | null // FK a businesses.id
  name: string
  city: string
  state: string
  address: string | null
  phone: string | null
  wa_number: string | null
  timezone: string
  active: boolean
  config: Record<string, unknown> // StoreConfig como JSONB
  synced_at: number // Date.now()
}

// ── LocalUserBusinessAccess ────────────────────────────────────────────────
// Caché offline de user_business_access — membresía usuario ↔ empresa.
export interface LocalUserBusinessAccess {
  id: string
  user_id: string
  business_id: string
  role: string
  permissions: string[] // Permission[]
  is_primary: boolean
  active: boolean
  updated_at: string // ISO string
  synced_at: number // Date.now()
}

// ── LocalUserStoreAccess ───────────────────────────────────────────────────
// Caché offline de user_store_access — acceso usuario ↔ tienda.
export interface LocalUserStoreAccess {
  id: string
  user_id: string
  store_id: string
  business_id: string | null
  role: string | null
  permissions: string[] // Permission[]
  is_primary: boolean
  active: boolean
  updated_at: string // ISO string
  synced_at: number // Date.now()
}

// ── LocalFeatureFlag ───────────────────────────────────────────────────────
export interface LocalFeatureFlag {
  key: string
  enabled: boolean
  enabled_for_stores: string[]
  enabled_for_roles: string[]
  synced_at: number // Date.now()
}

// ── LocalSale ──────────────────────────────────────────────────────────────
export interface LocalSale {
  id: string // sale UUID de Supabase (o temp key si offline)
  idempotency_key: string // UUID v4 generado en cliente
  folio: string
  store_id: string
  user_id: string
  shift_id: string
  client_id: string | null
  client_name: string | null
  total: number
  status: 'completed' | 'returned' | 'pending_sync'
  created_at: string // ISO string
  synced_at: number | null // null si aún no sincronizado
  items: Array<{
    product_id: string | null
    name: string
    qty: number
    unit_price: number
    unit_cost: number
    discount: number
    total: number
  }>
  payments: Array<{
    method: string
    amount: number
  }>
}

// ── OutboxEntry ─────────────────────────────────────────────────────────────
export type OutboxStatus = 'pending' | 'syncing' | 'done' | 'failed'

export interface OutboxEntry {
  id: string // UUID v4 generado en cliente (crypto.randomUUID())
  idempotency_key: string // UUID v4 único — previene duplicados en servidor
  endpoint: string // URL de la Edge Function
  method: 'POST' | 'PUT' | 'PATCH'
  payload: Record<string, unknown>
  status: OutboxStatus
  attempts: number
  max_attempts: number // Máximo de reintentos antes de 'failed'
  last_error: string | null
  last_attempt_at: number | null // Date.now()
  created_at: number // Date.now()
  done_at: number | null // Date.now() cuando procesado exitosamente
  entity_type: string | null // Para debugging: 'sale', 'shift', etc.
  entity_local_id: string | null // ID local de la entidad
  store_id: string | null // Para contexto multi-tienda
  user_id: string | null // Quién generó la operación
}

// ── OfflineSaleIntent (P0-1 · Fase 8B) ──────────────────────────────────────
// INTENCIÓN de venta capturada sin conexión. NO es una venta. NUNCA lleva firma,
// folio server ni total autoritativo generados offline. El servidor recalcula y
// confirma en fases posteriores. En 8B solo se usan draft/queued/cancelled.
export type OfflineSaleIntentStatus =
  | 'draft'
  | 'queued'
  | 'syncing'
  | 'confirmed'
  | 'rejected'
  | 'conflict'
  | 'expired'
  | 'cancelled'
  // P0-1 · Fase 8D — revisión/resolución de conflictos.
  | 'under_review'
  | 'escalated'
  | 'resolved_cancelled'
  | 'resolved_adjustment'

export interface OfflineSaleIntent {
  local_id: string // UUID cliente — PK
  idempotency_key: string // UUID único — clave server (dedup)
  device_id: string
  user_id: string
  store_id: string
  business_id: string
  shift_id: string
  occurred_at: string // ISO — hora REAL de captura offline
  cart_snapshot: Array<{
    product_id: string
    quantity: number
    requested_discount: number
    unit_ids?: string[]
  }>
  requested_payments: Array<{ method: 'efectivo'; amount: number }>
  authorization_ref: string | null // 8B: siempre null
  status: OfflineSaleIntentStatus
  attempts: number
  last_error_code: string | null
  last_attempt_at: number | null
  expires_at: string // ISO — occurred_at + ventana (8h)
  server_sale_id: string | null
  server_folio: string | null
  server_total: number | null
  // P0-1 · Fase 8C — datos de confirmación server-authoritative (no indexados).
  server_paid?: number | null
  server_change?: number | null
  signature_status?: string | null
  conflict_code?: string | null
  confirmed_at?: number | null
  // P0-1 · Fase 8D — resolución de conflicto (referencia server, no indexado).
  resolution_id?: string | null
  resolution_type?: string | null
  cash_reconciliation_status?: string | null
  cash_received_declared?: number | null
  resolved_at?: number | null
  provisional_reference: string // "PENDIENTE · <6 chars>" — NO es folio
  created_at: number // Date.now()
  updated_at: number
}

// ── AppMeta (P0-1 · Fase 8B) ────────────────────────────────────────────────
// Metadatos locales del dispositivo/instalación (device_id, persistencia, etc.).
export interface AppMeta {
  key: string
  value: string
  updated_at: number
}

// ── LocalReservation ────────────────────────────────────────────────────────
export interface LocalReservation {
  id?: number
  reservation_id: string | null // UUID del servidor (null hasta confirmar)
  product_id: string
  store_id: string
  qty: number
  cart_id: string
  expires_at: number // Date.now() + TTL
  consumed: boolean
  released: boolean
  created_at: number // Date.now()
}

// ── SyncMeta ────────────────────────────────────────────────────────────────
export interface SyncMeta {
  table_name: string
  last_sync_at: number // Date.now()
  etag: string | null
}

// ── SyncRun (ESCALA 0A/0B) ───────────────────────────────────────────────────
// Estado RESUMIBLE de una corrida de sincronización paginada, identificada por un
// SCOPE seguro (entity + userId + business + store + schemaVersion). ESCALA 0B
// separa explícitamente TRES estados (Bloque 2):
//   A. RESUME cursor — dónde continuar dentro de una corrida incompleta.
//   B. SNAPSHOT boundary — frontera server-side de la corrida actual.
//   C. COMPLETED checkpoint — último punto confirmado; origen de la próxima
//      sincronización incremental. Solo se promueve al COMPLETAR.
// NUNCA se marca 'completed' (ni se promueve el checkpoint) hasta hasMore=false.
export type SyncRunStatus =
  | 'pending'
  | 'running'
  | 'interrupted'
  | 'failed'
  | 'completed'
  | 'invalidated'

export type SyncRunMode = 'incremental' | 'full_reconciliation'

export interface SyncRun {
  id: string // scopeFingerprint(scope) — ver lib/sync/paginate.ts
  entity: string
  user_id: string // auth/user id estable (NO email) — aísla runs entre usuarios
  business_id: string | null
  store_id: string | null
  mode: SyncRunMode
  // (B) Frontera de instantánea: SOLO se descargan filas con updated_at <= boundary.
  // Autoridad del SERVIDOR (no reloj del cliente).
  snapshot_boundary: string | null // ISO timestamptz del servidor
  // (A) RESUME cursor keyset (updated_at, id): temporal, dónde continuar la corrida.
  resume_cursor_updated_at: string | null
  resume_cursor_id: string | null
  // (C) COMPLETED checkpoint (updated_at, id): confirmado, origen del próximo delta.
  completed_checkpoint_updated_at: string | null
  completed_checkpoint_id: string | null
  pages_completed: number
  records_received: number
  records_upserted: number
  status: SyncRunStatus
  last_error: string | null
  retry_count: number
  started_at: number // Date.now()
  completed_at: number | null // Date.now() al llegar a hasMore=false
  schema_version: number // invalida la corrida/checkpoint si cambia el contrato local
  /**
   * Token único del INTENTO que escribió esta fila (0B.11). Permite compare-and-swap:
   * una corrida sólo puede escribir el registro si sigue siendo el intento vigente. Sin
   * esto, un dueño que perdió el lease podía escribir `interrupted` DESPUÉS de que otra
   * pestaña ya hubiera adquirido el lease y arrancado su propia corrida, pisando su
   * estado `running`/`completed`. No es índice: no requiere subir la versión de Dexie.
   */
  attempt_id?: string
  /**
   * FENCING TOKEN monotónico por scope (0B.13). Se incrementa en cada adquisición
   * LEGÍTIMA mediante compare-and-swap transaccional (observado → observado+1), así que
   * define un ORDEN TOTAL de propiedad sin depender del reloj del cliente.
   *
   * Sustituye por completo a la heurística previa basada en `started_at`, que no era
   * un CAS: dos intentos con el mismo `Date.now()` empataban y el viejo podía reclamar
   * el registro del nuevo. Toda escritura posterior exige mismo `attempt_id` Y mismo
   * `fencing_token`; un intento con token inferior no puede reclamar, completar,
   * interrumpir, marcar error, mover checkpoint ni frescura.
   */
  fencing_token?: number
}

// ── LocalErrorLog ───────────────────────────────────────────────────────────
export interface LocalErrorLog {
  id?: number
  level: 'error' | 'warn' | 'info'
  message: string
  stack: string | null
  context: string | null // JSON string
  created_at: number // Date.now()
  synced: boolean
}

// ── LocalLayaway ───────────────────────────────────────────────────────────
export interface LocalLayaway {
  id: string
  folio: string
  client_id: string
  store_id: string
  total_amount: number
  down_payment: number
  paid_amount: number
  balance: number
  due_date: string | null
  status: 'active' | 'ready' | 'delivered' | 'cancelled' | 'pending_sync'
  notes: string | null
  created_at: string
  synced_at: number | null
  items: Array<{
    product_id: string | null
    name: string
    qty: number
    unit_price: number
    total: number
  }>
}

// ── LocalQuote ─────────────────────────────────────────────────────────────
export interface LocalQuote {
  id: string
  folio: string
  client_id: string | null
  store_id: string
  subtotal: number
  discount: number
  tax: number
  total: number
  valid_until: string | null
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'pending_sync'
  notes: string | null
  created_at: string
  synced_at: number | null
  items: Array<{
    product_id: string | null
    name: string
    qty: number
    unit_price: number
    total: number
  }>
}

// ── LocalOrder ─────────────────────────────────────────────────────────────
export interface LocalOrder {
  id: string
  folio: string
  client_id: string | null
  store_id: string
  subtotal: number
  discount: number
  tax: number
  total: number
  deposit: number
  reserve_stock: boolean
  promised_date: string | null
  status:
    | 'pending'
    | 'confirmed'
    | 'processing'
    | 'ready'
    | 'delivered'
    | 'cancelled'
    | 'pending_sync'
  notes: string | null
  created_at: string
  synced_at: number | null
  items: Array<{
    product_id: string | null
    name: string
    qty: number
    unit_price: number
    total: number
  }>
}

// ── LocalCredit ────────────────────────────────────────────────────────────
export interface LocalCredit {
  id: string
  client_id: string
  store_id: string
  sale_id: string | null
  original_amount: number
  down_payment: number
  paid_amount: number
  balance: number // original - down - paid (calculado local)
  due_date: string | null // ISO date string
  interest_rate: number
  notes: string | null
  status: 'active' | 'overdue' | 'paid' | 'cancelled'
  created_at: string
  paid_at: string | null
  synced_at: number | null
}

// ── LocalCreditPayment ─────────────────────────────────────────────────────
export interface LocalCreditPayment {
  id: string
  credit_id: string
  amount: number
  method: string
  reference: string | null
  notes: string | null
  created_at: string
  synced_at: number | null
}

// ── CelmaxxDB ────────────────────────────────────────────────────────────────

export class CelmaxxDB extends Dexie {
  products!: EntityTable<LocalProduct, 'id'>
  stock!: EntityTable<LocalStock, 'id'>
  clients!: EntityTable<LocalClient, 'id'>
  categories!: EntityTable<LocalCategory, 'id'>
  shifts!: EntityTable<LocalShift, 'id'>
  sales!: EntityTable<LocalSale, 'id'>
  outbox!: Table<OutboxEntry, string>
  reservations!: EntityTable<LocalReservation, 'id'>
  feature_flags!: EntityTable<LocalFeatureFlag, 'key'>
  sync_meta!: EntityTable<SyncMeta, 'table_name'>
  // v8 — ESCALA 0A: estado resumible de sincronización paginada.
  sync_runs!: EntityTable<SyncRun, 'id'>
  error_logs!: EntityTable<LocalErrorLog, 'id'>
  credits!: EntityTable<LocalCredit, 'id'>
  credit_payments!: EntityTable<LocalCreditPayment, 'id'>
  layaways!: EntityTable<LocalLayaway, 'id'>
  quotes!: EntityTable<LocalQuote, 'id'>
  orders!: EntityTable<LocalOrder, 'id'>
  // v4 — Corporate Engine E1.2
  businesses!: EntityTable<LocalBusiness, 'id'>
  stores!: EntityTable<LocalStore, 'id'>
  // v5 — Corporate Engine E1.3
  userBusinessAccess!: EntityTable<LocalUserBusinessAccess, 'id'>
  userStoreAccess!: EntityTable<LocalUserStoreAccess, 'id'>
  // v7 — P0-1 Fase 8B: intenciones de venta offline + metadatos del dispositivo
  offline_sale_intents!: EntityTable<OfflineSaleIntent, 'local_id'>
  app_meta!: EntityTable<AppMeta, 'key'>

  constructor() {
    super('CelmaxxPOS')

    this.version(1).stores({
      // Todos los índices usan snake_case para coincidir con las interfaces
      products: '&id, name, code, barcode, category_id, featured, active, synced_at',

      stock: '&id, product_id, store_id, [product_id+store_id], available, synced_at',

      clients: '&id, &phone_e164, name, level, active, synced_at',

      categories: '&id, parent_id, sort_order, active, synced_at',

      shifts: '&id, store_id, user_id, status, opened_at',

      sales: '&id, &idempotency_key, store_id, shift_id, client_id, status, created_at, synced_at',

      outbox:
        '&id, &idempotency_key, status, attempts, created_at, entity_type, store_id, [status+created_at]',

      reservations:
        '++id, cart_id, product_id, [product_id+store_id], expires_at, consumed, released',

      feature_flags: '&key, enabled, synced_at',

      sync_meta: '&table_name, last_sync_at',

      error_logs: '++id, level, created_at, synced',
    })

    // v2: Créditos y abonos
    // ⚠ DEFINICIÓN MUERTA. Ninguna de estas dos tablas se lee ni se escribe ya: la deuda y los
    // abonos vienen del servidor (public.credits / public.credit_payments). `credit_payments`
    // quedó sin escritor en 2.5-C1 y `credits` en 2.6-C, al retirar el alta manual de crédito.
    // Se conservan por no forzar una versión de esquema; NO son fuente financiera de nada.
    this.version(2).stores({
      credits: '&id, client_id, store_id, sale_id, status, due_date, synced_at',
      credit_payments: '&id, credit_id, created_at, synced_at',
    })

    // v3: Paquete comercial (apartados, cotizaciones, pedidos)
    this.version(3).stores({
      layaways: '&id, client_id, store_id, status, synced_at',
      quotes: '&id, client_id, store_id, status, synced_at',
      orders: '&id, client_id, store_id, status, synced_at',
    })

    // v4: Corporate Engine E1.2 — empresas y caché offline de tiendas
    this.version(4).stores({
      businesses: '&id, brand, status, synced_at',
      stores: '&id, business_id, brand, code, active, synced_at',
    })

    // v5: Corporate Engine E1.3 — acceso corporativo de usuarios
    this.version(5).stores({
      userBusinessAccess: '&id, user_id, business_id, active, synced_at',
      userStoreAccess: '&id, user_id, store_id, business_id, active, synced_at',
    })

    // v6: Catálogo por empresa — business_id en products y categories
    this.version(6).stores({
      products: '&id, name, code, barcode, category_id, business_id, featured, active, synced_at',
      categories: '&id, business_id, parent_id, sort_order, active, synced_at',
    })

    // v7: P0-1 Fase 8B — intenciones de venta offline + metadatos del dispositivo.
    // Upgrade aditivo: no altera tablas previas ni sus datos.
    // No se indexan objetos JSON (cart_snapshot / requested_payments).
    this.version(7).stores({
      offline_sale_intents:
        '&local_id, &idempotency_key, status, device_id, user_id, store_id, business_id, shift_id, occurred_at, expires_at, [device_id+user_id+store_id], [status+occurred_at]',
      app_meta: '&key, updated_at',
    })

    // v8: ESCALA 0A — estado resumible de sincronización paginada por entidad+alcance.
    // Upgrade aditivo: no altera tablas previas ni sus datos.
    this.version(8).stores({
      sync_runs: '&id, entity, business_id, store_id, status, started_at',
    })

    // v9: ESCALA 0B — indexa user_id (aislamiento por usuario + purge en logout).
    // Aditivo: solo añade un índice; el objeto guardado ya trae user_id.
    this.version(9).stores({
      sync_runs: '&id, entity, user_id, business_id, store_id, status, started_at',
    })

    // v10 (0B.9 P1#5): alcance en clientes. Los registros previos quedan sin
    // business_id/store_id; el filtro los trata como NO pertenecientes al alcance
    // activo (se ocultan) y la primera reconciliación completa los repuebla o purga.
    this.version(10).stores({
      clients: '&id, &phone_e164, name, level, active, store_id, business_id, synced_at',
    })

    // v11 (0B.10 P2): el teléfono NO es único globalmente. El servidor lo declara
    // único por (phone_e164, store_id) WHERE deleted_at IS NULL (migr 00053): el mismo
    // número puede existir en T01 y T02 con historial separado. Dexie lo tenía como
    // `&phone_e164` (único GLOBAL), así que al cachear clientes de dos tiendas con el
    // mismo teléfono `bulkPut` lanzaba ConstraintError y —con el cierre atómico de
    // 0B.9— abortaba toda la promoción, dejando la sincronización rota de forma
    // permanente para ese alcance. Se pasa a índice NO único y se añade el compuesto
    // [phone_e164+store_id], que es la unicidad REAL del servidor.
    this.version(11).stores({
      clients:
        '&id, phone_e164, [phone_e164+store_id], name, level, active, store_id, business_id, synced_at',
    })
    // v12 — P0 (00221): purga el costo que quedó cacheado en instalaciones previas.
    // `cost` nunca vuelve a viajar al cliente (proyección de sync sin cost). El índice
    // de `products` no incluía `cost`, así que sólo se limpia el CAMPO de cada registro.
    // Idempotente; no toca carrito, ventas pendientes ni la cola offline (otras stores).
    this.version(12)
      .stores({}) // sin cambios de índices
      .upgrade(async tx => {
        await tx
          .table('products')
          .toCollection()
          .modify(p => {
            if (p && 'cost' in p) delete (p as Record<string, unknown>).cost
          })
      })
  }
}

export const db = new CelmaxxDB()

/**
 * ¿Este cliente es consultable en el alcance activo? (0B.9 P1#5 → 0B.10 P2)
 *
 * MODELO: el cliente pertenece a su TIENDA DE ORIGEN (`store_id`, la del servidor,
 * donde se dio de alta y donde vive su historial y su unicidad de teléfono), pero es
 * CONSULTABLE por cualquier tienda autorizada de la MISMA EMPRESA — un cliente
 * corporativo dado de alta en T01 debe poder atenderse en T02. La frontera dura es la
 * EMPRESA: clientes de otra empresa (u otra sesión) nunca se mezclan ni se muestran.
 *
 * El filtro es LOCAL, así que al cambiar de empresa la PII no autorizada desaparece
 * de inmediato incluso OFFLINE; la purga física llega en la siguiente reconciliación.
 * Los registros sin alcance (cacheados antes de la v10) se tratan como FUERA de
 * alcance: se ocultan hasta que una reconciliación los repueble o purgue, que es el
 * lado seguro para datos personales.
 */
export function clientInScope(
  c: LocalClient,
  businessId: string | null,
  authorizedStoreIds: ReadonlySet<string> | null
): boolean {
  if (!businessId) return false
  if (c.business_id !== businessId) return false
  // 0B.11 P1: la empresa NO basta. Un cliente sólo es consultable si su tienda de
  // ORIGEN está entre las que ESTE usuario tiene autorizadas (su tienda activa más las
  // de user_store_access). Antes bastaba con compartir empresa, así que un usuario de
  // alcance reducido podía consultar clientes de tiendas que no opera, cacheados por
  // otro usuario. Sin lista de autorizadas no se muestra nada (lado seguro).
  if (!authorizedStoreIds) return false
  return c.store_id != null && authorizedStoreIds.has(c.store_id)
}

/**
 * Tiendas que el usuario tiene autorizadas localmente: la tienda activa + las de
 * `user_store_access` activas para ese usuario. Es la base del filtro de PII de
 * clientes; se recalcula al cambiar de usuario o de tienda.
 */
export async function authorizedStoreIdsFor(
  userId: string | null,
  activeStoreId: string | null
): Promise<Set<string>> {
  const ids = new Set<string>()
  if (activeStoreId) ids.add(activeStoreId)
  if (!userId) return ids
  const rows = await db.userStoreAccess
    .filter(a => a.user_id === userId && a.active)
    .toArray()
    .catch(() => [])
  for (const a of rows) ids.add(a.store_id)
  return ids
}

/** Abre la BD y limpia logs viejos */
export async function initDB(): Promise<void> {
  try {
    await db.open()
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000
    await db.error_logs.where('created_at').below(cutoff).delete()
  } catch (error) {
    console.error('[Dexie] Error al abrir BD:', error)
    if (error instanceof Dexie.OpenFailedError) {
      await Dexie.delete('CelmaxxPOS')
      window.location.reload()
    }
    throw error
  }
}

/**
 * Purga SOLO el catálogo operativo cacheado en Dexie: products + stock.
 *
 * Uso:
 *  - Al cambiar de empresa/tienda (setActiveContext), para evitar que el
 *    catálogo de una empresa quede visible al entrar a otra.
 *  - Como purga manual de productos "fantasma" escritos en cliente que la DB
 *    nunca confirmó (p.ej. alta de producto cuyo inventory_balances falló).
 *
 * NO toca: Supabase/DB, clientes, ventas, turnos, reservaciones, outbox ni auth.
 * El catálogo correcto se re-hidrata solo vía get_pos_products en el próximo sync.
 */
export async function clearCatalogCache(): Promise<void> {
  await db.transaction('rw', [db.products, db.stock], async () => {
    await Promise.all([db.products.clear(), db.stock.clear()])
  })
}

/** Limpia cache local sin tocar el outbox */
export async function clearLocalCache(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.products,
      db.stock,
      db.clients,
      db.categories,
      db.shifts,
      db.sales,
      db.reservations,
      db.feature_flags,
      db.sync_meta,
    ],
    async () => {
      await Promise.all([
        db.products.clear(),
        db.stock.clear(),
        db.clients.clear(),
        db.categories.clear(),
        db.shifts.clear(),
        db.sales.clear(),
        db.reservations.clear(),
        db.feature_flags.clear(),
        db.sync_meta.clear(),
      ])
    }
  )
}

/**
 * Invalida el estado de sincronización (sync_runs + sync_meta) SIN tocar el outbox
 * ni las intenciones offline pendientes. Se llama en logout/cambio de usuario para
 * que ninguna corrida/cursor/checkpoint de una identidad se reanude en otra
 * (ESCALA 0B, Bloque 8). El catálogo se re-hidrata en el próximo contexto.
 */
export async function clearSyncState(): Promise<void> {
  // 0B.11 P1: al cambiar de usuario (o cerrar sesión) también se PURGA la PII de
  // clientes. Antes sólo se limpiaban sync_runs/sync_meta, así que la caché de clientes
  // que había descargado un usuario con mayor alcance seguía en el dispositivo y otro
  // usuario de la misma empresa podía consultarla offline (el filtro local sólo miraba
  // business_id). La caché correcta se re-hidrata en la primera reconciliación del
  // usuario nuevo, ya acotada a SUS tiendas autorizadas. No se toca outbox ni ventas.
  await db.transaction('rw', [db.sync_runs, db.sync_meta, db.clients], async () => {
    await Promise.all([db.sync_runs.clear(), db.sync_meta.clear(), db.clients.clear()])
  })
}

/** Borra toda la BD. USAR CON PRECAUCIÓN. */
export async function nukLocalDB(): Promise<void> {
  await db.delete()
  console.warn('[Dexie] BD local eliminada completamente')
}

export type { Dexie }
